import { z } from 'zod';
import {
    ResourceLibraryArticleBaseItem,
    ResourceLibraryArticleItem,
    ResourceLibraryArticleItemContentSection,
} from '../types/resourceLibraryItems';
import {
    GetResourceLibraryArticleItemSchema,
    ResourceLibraryArticleBaseItemSchema,
    ResourceLibraryArticleItemSchema,
} from '../schemas/resourceLibraryArticleItemZodSchemas';
import {
    ProductGroupSchema,
    ProductApplicationSchema,
    NavigationNodeSchema,
} from '../schemas/resourceLibraryBaseZodSchemas';

export function hydrateResourceLibraryArticleBaseItem(
    item: z.infer<typeof ResourceLibraryArticleBaseItemSchema>
): ResourceLibraryArticleBaseItem {
    const articleBaseItem: ResourceLibraryArticleBaseItem = {
        kind: 'article',
        id: item.id,
        key: item.key,
        name: item.name,
        description: item.description ?? '', // Description is nullable
        brandId: item.brand ? item.brand.id : null,
        productGroupIds: item.productGroup
            ? item.productGroup.map((pg: z.infer<typeof ProductGroupSchema>) => pg.id)
            : [],
        productApplicationIds: item.productApplication
            ? item.productApplication.map((pa: z.infer<typeof ProductApplicationSchema>) => pa.id)
            : [],
        resourceTypeId: item.resourceType.id,
        navigationScopeIds: item.navigationScope
            ? item.navigationScope.map((ns: z.infer<typeof NavigationNodeSchema>) => ns.id)
            : [],
        exclusiveNavigationScopeIds: item.exclusiveNavigationScope
            ? item.exclusiveNavigationScope.map(
                  (ens: z.infer<typeof NavigationNodeSchema>) => ens.id
              )
            : [],
        publicationDate: item.publicationDate,
        thumbnailUrl: item.thumbnailUrl,
    };

    return articleBaseItem;
}

export function hydrateResourceLibraryArticleItem(
    item: z.infer<typeof ResourceLibraryArticleItemSchema>
): ResourceLibraryArticleItem {
    const articleItem = hydrateResourceLibraryArticleBaseItem(item);
    return {
        ...articleItem,
        coverImageUrl: item.coverImage?.fullpath ?? null,
        contentSections: item.sections.map((section) => {
            const contentSection: ResourceLibraryArticleItemContentSection = {
                level: section.level,
                title: section.title,
                content: section.content,
            };
            return contentSection;
        }),
        navigationNodeId: item.navigationNode?.id ?? null,
    };
}

/**
 * Transforms and validates the response from the getResourceLibraryArticleItem GraphQL query
 * to a simplified ResourceLibraryArticleItem type.
 *
 * @param data The response data from the getResourceLibraryArticleItem query
 * @returns An array of valid ResourceLibraryArticleItem objects
 */
export function transformResourceLibraryArticleItemResponse(
    data: unknown // GetResourceLibraryArticleItemQuery - use unknown for jest tests be able to
    // pass invalid data
): ResourceLibraryArticleItem | null {
    // Parse and validate the data using the Zod schema
    const parsedResult = GetResourceLibraryArticleItemSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getResourceLibraryArticleItem:',
            parsedResult.error.errors
        );
        return null;
    }

    const item = parsedResult.data.getResourceLibraryArticleItem;
    return hydrateResourceLibraryArticleItem(item);
}
